import { useState } from 'react'
import { Download, Loader2, Users } from 'lucide-react'
import { opcoApi } from '../../../../services/api'
import { useToast } from '../../../../context/ToastContext'
import { ConfirmDialog, cardCls, errorMessage, primaryBtn, secondaryBtn } from '../shared'
import { canApply, planSummary, reasonLabel, selectedCount, toggleExcluded } from './helpers'

// "Place existing people": the people who are already in the app but have no OpCo. Preview first
// (nothing is written), untick anyone who should stay out, then Apply. Every run can be undone
// from the list of runs below. The rules count here whether they are switched on or off.
export default function BulkAssignPanel({ onApplied }) {
  const toast = useToast()
  const [asManagers, setAsManagers] = useState(false)
  const [plan, setPlan] = useState(null)
  const [excluded, setExcluded] = useState(() => new Set())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [confirming, setConfirming] = useState(false)
  const [applying, setApplying] = useState(false)
  const [showNeeds, setShowNeeds] = useState(false)

  const preview = async (managers = asManagers) => {
    setLoading(true)
    setError(null)
    try {
      const res = await opcoApi.previewAutoAssign(managers)
      setPlan(res?.data || null)
      setExcluded(new Set())
    } catch (err) {
      setError(errorMessage(err, 'Could not build the preview'))
    } finally {
      setLoading(false)
    }
  }

  const changeManagers = (checked) => {
    setAsManagers(checked)
    if (plan) preview(checked)
  }

  const downloadCsv = async () => {
    try {
      const res = await opcoApi.downloadAutoAssignCsv(asManagers)
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }))
      const a = document.createElement('a')
      a.href = url
      a.download = 'opco-auto-assign-preview.csv'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      toast.error('Could not download', errorMessage(err))
    }
  }

  const apply = async () => {
    setApplying(true)
    try {
      const res = await opcoApi.applyAutoAssign({
        plan_hash: plan.plan_hash,
        admins_as_managers: asManagers,
        excluded_user_ids: [...excluded],
      })
      const run = res?.data?.run || {}
      toast.success('People placed', `${run.added ?? 0} added${run.skipped ? `, ${run.skipped} skipped` : ''}${run.failed ? `, ${run.failed} failed` : ''}. You can undo this below.`)
      setConfirming(false)
      setPlan(null)
      setExcluded(new Set())
      onApplied?.()
    } catch (err) {
      const code = err?.response?.data?.code
      setConfirming(false)
      if (code === 'stale_preview' && err.response.data.plan) {
        setPlan(err.response.data.plan)
        setExcluded(new Set())
        toast.warning('The list changed', 'Someone or something changed since you previewed. Check the new list and apply again.')
      } else if (code === 'run_in_progress') {
        toast.warning('Already running', 'Another apply or undo is running. Try again in a moment.')
      } else {
        toast.error('Could not place people', errorMessage(err))
      }
    } finally {
      setApplying(false)
    }
  }

  const rows = plan?.rows || []
  const needs = plan?.needs_decision || []
  const more = plan?.summary?.more_after_this_run || 0

  return (
    <section className={cardCls} aria-label="Place existing people">
      <div className="border-b border-gray-100 dark:border-gray-800 px-5 py-4">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Place existing people</h2>
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Finds everyone who is already here but has no OpCo and shows where the rules would put them. Nothing changes until you apply, and you can undo each run.
          Rules count here even when they are switched off.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 px-5 py-4">
        <button type="button" onClick={() => preview()} disabled={loading} className={primaryBtn}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {plan ? 'Refresh preview' : 'Preview'}
        </button>
        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <input type="checkbox" checked={asManagers} onChange={(e) => changeManagers(e.target.checked)} />
          Organisation Admins join as OpCo managers (otherwise everyone joins as a plain member)
        </label>
      </div>
      {error && <p role="alert" className="px-5 pb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {plan && (
        <div className="border-t border-gray-100 dark:border-gray-800">
          <div className="flex flex-wrap items-center gap-3 px-5 py-3">
            <p className="flex-1 text-sm font-medium text-gray-900 dark:text-gray-100" role="status">{planSummary(plan, excluded)}</p>
            <button type="button" onClick={downloadCsv} className={secondaryBtn}>
              <Download className="h-4 w-4" /> Download CSV
            </button>
            <button type="button" onClick={() => setConfirming(true)} disabled={!canApply(plan, excluded, applying)} className={primaryBtn}>
              Apply
            </button>
          </div>
          {more > 0 && (
            <p className="px-5 pb-2 text-xs text-amber-700 dark:text-amber-400">
              {more} more {more === 1 ? 'person is' : 'people are'} waiting: one run places up to 500. Preview again after applying.
            </p>
          )}
          <p className="px-5 pb-2 text-xs text-gray-500 dark:text-gray-400">
            An edited CSV can be loaded through "Import CSV" on the Members panel, but that import cannot be undone from here.
          </p>
          {rows.length > 0 && (
            <div className="max-h-96 overflow-auto px-5 pb-4">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase text-gray-500">
                  <tr>
                    <th className="py-1 pr-3">Add</th>
                    <th className="py-1 pr-3">Person</th>
                    <th className="py-1 pr-3">OpCo</th>
                    <th className="py-1 pr-3">Role</th>
                    <th className="py-1">Rule</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {rows.map((r) => (
                    <tr key={r.user_id}>
                      <td className="py-1.5 pr-3">
                        <input
                          type="checkbox"
                          aria-label={`Add ${r.email}`}
                          checked={!excluded.has(r.user_id)}
                          onChange={() => setExcluded((prev) => toggleExcluded(prev, r.user_id))}
                        />
                      </td>
                      <td className="py-1.5 pr-3 text-gray-900 dark:text-gray-100">
                        {r.email}
                        {r.org_admin && <span className="ml-2 text-xs text-gray-500">Admin</span>}
                        {r.invite_pending && <span className="ml-2 text-xs text-gray-500">invite pending</span>}
                      </td>
                      <td className="py-1.5 pr-3 font-mono text-xs">{r.opco_code}</td>
                      <td className="py-1.5 pr-3">{r.role}</td>
                      <td className="py-1.5 text-xs text-gray-500">{r.rule_enabled ? 'on' : 'off'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {needs.length > 0 && (
            <div className="border-t border-gray-100 dark:border-gray-800 px-5 py-3">
              <button type="button" onClick={() => setShowNeeds((v) => !v)} aria-expanded={showNeeds} className="text-xs font-semibold text-navy-700 dark:text-navy-300 hover:underline">
                {showNeeds ? 'Hide' : 'Show'} {needs.length} {needs.length === 1 ? 'person' : 'people'} who need a decision (not added)
              </button>
              {showNeeds && (
                <ul className="mt-2 max-h-72 divide-y divide-gray-100 overflow-auto text-sm dark:divide-gray-800">
                  {needs.map((n) => (
                    <li key={n.user_id} className="flex flex-wrap gap-x-3 py-1.5">
                      <span className="text-gray-900 dark:text-gray-100">{n.email}</span>
                      <span className="text-xs text-gray-500">{reasonLabel(n.reason)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {confirming && (
        <ConfirmDialog
          title="Place these people?"
          message={`${planSummary(plan, excluded)}. They get access straight away. You can undo this run from the list below.`}
          confirmLabel={`Add ${selectedCount(plan, excluded)}`}
          busy={applying}
          onConfirm={apply}
          onCancel={() => setConfirming(false)}
        />
      )}
    </section>
  )
}
